import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  full_name: "",
  email: "",
  company: "",
  role: "",
  category: "",
  philosophy: "",
};

const POINTS = [
  "Sit alongside the founders who shaped this industry.",
  "Your chapter, your voice — final approval stays with you.",
  "Reach the boardrooms that cold outreach can\u2019t open.",
];

export default function ApplicationForm() {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "A valid corporate email is required.";
    if (form.company.trim().length < 2) e.company = "Company is required.";
    if (form.role.trim().length < 2) e.role = "Your role is required.";
    if (!form.category) e.category = "Please pick one.";
    if (form.philosophy.trim().length < 10) e.philosophy = "Give us a few lines (10+ chars).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      // Map UI fields to existing backend schema
      const payload = {
        full_name: form.full_name.trim(),
        corporate_email: form.email.trim(),
        company_role: `${form.company.trim()} — ${form.role.trim()} (${form.category})`,
        linkedin_profile: "not-provided",
        philosophy: form.philosophy.trim(),
      };
      await axios.post(`${API}/applications`, payload);
      setSuccess(true);
      toast.success("Application received. We'll follow up directly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(detail || "This email has already been used for an application.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Submission failed. Please retry.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section
        id="apply"
        data-testid="application-section"
        className="relative py-20 lg:py-28"
        style={{ backgroundColor: "var(--ink)" }}
      >
        <div className="max-w-3xl mx-auto container-x text-center text-cream">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            data-testid="application-success"
          >
            <p className="font-mono text-[10px] tracking-[0.28em] text-gold-bright mb-8">
              APPLICATION RECEIVED
            </p>
            <h2 className="font-display font-semibold text-[clamp(2rem,5vw,3.8rem)] leading-[1.05] tracking-[-0.015em] mb-8">
              Thanks. We&rsquo;ll follow up within two weeks.
            </h2>
            <p className="text-[15px] lg:text-[17px] leading-[1.8] text-cream/70 max-w-xl mx-auto">
              Our editorial team reads every submission personally. If your
              company&rsquo;s story is a fit for Vol. I, you&rsquo;ll receive a
              calendar link to schedule the interview directly.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="apply"
      data-testid="application-section"
      className="relative py-16 lg:py-24 text-cream"
      style={{ backgroundColor: "var(--ink)" }}
    >
      <div className="max-w-[1400px] mx-auto container-x">
        {/* eyebrow */}
        <div className="flex items-center gap-4 mb-14">
          <span className="font-mono text-[10px] tracking-[0.28em] text-gold-bright">APPLY</span>
          <span className="flex-1 h-px bg-cream/15" />
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="font-display font-semibold text-[clamp(2rem,5.2vw,4.2rem)] leading-[1.05] tracking-[-0.015em] max-w-[22ch] mb-10"
        >
          Reserve your chapter. Tell your story the way it deserves to be told.
        </motion.h2>

        <p className="text-[15px] lg:text-[17px] leading-[1.8] text-cream/70 max-w-2xl mb-20 lg:mb-24">
          Apply below. Every submission is read personally by our editorial
          team &mdash; and the founders whose stories belong in Vol.&nbsp;I
          will hear from us directly.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* left rail: trust points */}
          <div className="lg:col-span-4">
            <ul className="space-y-5">
              {POINTS.map((p, i) => (
                <li key={p} className="flex gap-4 items-start">
                  <span className="font-mono text-[11px] text-gold-bright mt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[14px] leading-[1.7] text-cream/85">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* right: form */}
          <div className="lg:col-span-8">
            <div
              className="flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[10px] tracking-[0.18em] text-gold-bright/80 uppercase mb-5"
              data-testid="trusted-by-strip"
            >
              <span className="text-cream/50">Trusted by</span>
              <span className="h-px w-6 bg-cream/20" />
              <span>PPAI 100 leaders</span>
              <span className="text-cream/30">&middot;</span>
              <span>IGC Global</span>
              <span className="text-cream/30">&middot;</span>
              <span>Consortium Gifts</span>
            </div>
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.1 }}
              onSubmit={onSubmit}
              noValidate
              data-testid="application-form"
              className="border border-cream/25 bg-white/[0.02] p-6 sm:p-10"
            >
              <Field
                label="Your Name"
                name="full_name"
                value={form.full_name}
                onChange={onChange}
                error={errors.full_name}
                placeholder="Full name"
                testid="input-full-name"
                autoComplete="name"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-8">
                <Field
                  label="Company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  error={errors.company}
                  placeholder="Company name"
                  testid="input-company"
                />
                <Field
                  label="Your Role"
                  name="role"
                  value={form.role}
                  onChange={onChange}
                  error={errors.role}
                  placeholder="e.g. Founder, CEO"
                  testid="input-role"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-8">
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  error={errors.email}
                  placeholder="you@company.com"
                  testid="input-email"
                  autoComplete="email"
                />
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.2em] text-cream/55 mb-3 uppercase">
                    Distributor or Supplier
                  </label>
                  <div className="relative">
                    <select
                      name="category"
                      value={form.category}
                      onChange={onChange}
                      data-testid="input-category"
                      className="w-full appearance-none bg-white/[0.04] border border-cream/30 px-4 py-3.5 text-[15px] text-cream font-body focus:outline-none focus:border-gold focus:bg-white/[0.07] transition-colors cursor-pointer"
                    >
                      <option value="" disabled className="bg-ink text-cream">
                        Select one
                      </option>
                      <option value="Distributor" className="bg-ink">Distributor</option>
                      <option value="Supplier" className="bg-ink">Supplier</option>
                      <option value="Both" className="bg-ink">Both</option>
                    </select>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-cream/50">▾</span>
                  </div>
                  {errors.category && (
                    <p className="mt-2 text-xs text-red-400 font-mono" data-testid="error-category">
                      {errors.category}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-8">
                <label
                  htmlFor="philosophy"
                  className="block font-mono text-[10px] tracking-[0.2em] text-cream/55 mb-3 uppercase"
                >
                  In a few lines — why this company&rsquo;s story belongs in the book
                </label>
                <textarea
                  id="philosophy"
                  name="philosophy"
                  rows={5}
                  value={form.philosophy}
                  onChange={onChange}
                  data-testid="input-philosophy"
                  placeholder="The founding moment, the turning point, the thing most people don't know..."
                  className="w-full bg-white/[0.04] border border-cream/30 px-4 py-3.5 text-[15px] text-cream font-body leading-[1.7] focus:outline-none focus:border-gold focus:bg-white/[0.07] transition-colors resize-none placeholder:text-cream/40"
                />
                {errors.philosophy && (
                  <p className="mt-2 text-xs text-red-400 font-mono" data-testid="error-philosophy">
                    {errors.philosophy}
                  </p>
                )}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-8 border-t border-cream/10">
                <p className="font-mono text-[10px] tracking-[0.2em] text-cream/40 uppercase">
                  We&rsquo;ll never share your details. PPAI 100 cohort only.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-application"
                  className="group inline-flex items-center gap-2 bg-cream text-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.12em] hover:bg-gold-bright transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                  <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, error, placeholder, testid, ...rest }) {
  return (
    <div>
      <label htmlFor={name} className="block font-mono text-[10px] tracking-[0.2em] text-cream/55 mb-3 uppercase">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/[0.04] border border-cream/30 px-4 py-3.5 text-[15px] text-cream font-body focus:outline-none focus:border-gold focus:bg-white/[0.07] transition-colors placeholder:text-cream/40"
        {...rest}
      />
      {error && (
        <p className="mt-2 text-xs text-red-400 font-mono" data-testid={`error-${name.replace(/_/g, "-")}`}>
          {error}
        </p>
      )}
    </div>
  );
}
